import { useEffect, useState } from "react";
import { countryOptions } from "../../lib/countries";
import { useTranslation } from "react-i18next";
import { Check, AlertTriangle } from "lucide-react";
import api, { extractAxiosError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const COUNTRY_FALLBACK = "IT";

const formFromUser = (user) => ({
  company_name: user?.company_name || "",
  vat_number: user?.vat_number || "",
  address: user?.address || "",
  country: user?.country || COUNTRY_FALLBACK,
});

export default function WorkshopProfileSection() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const countries = countryOptions(i18n.language, t("countries.ZZ"));
  const [form, setForm] = useState(() => formFromUser(user));
  // Re-sync when the profile is (re)loaded, so a stale user object can never
  // be saved back over the real country / VAT / address.
  useEffect(() => {
    setForm(formFromUser(user));
  }, [user]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const update = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setDone(false); };
  const incomplete = !user?.company_name || !user?.vat_number || !user?.address;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setDone(false); setBusy(true);
    try {
      await api.patch("/me/profile", form);
      await refresh();
      setDone(true);
    } catch (err) {
      setError(extractAxiosError(err, t("common.loadingError")));
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="workshop-profile-section">
      {incomplete && (
        <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs flex items-start gap-2" data-testid="profile-incomplete-hint">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5"/>
          <span>{t("settings.profileIncomplete")}</span>
        </div>
      )}
      <div>
        <label className="label-tech block mb-2">{t("auth.companyName")}</label>
        <input value={form.company_name} onChange={update("company_name")} maxLength={150} placeholder={t("auth.companyPlaceholder")} className="w-full px-4 py-2.5 rounded-md text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-blue-500" data-testid="profile-company"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label-tech block mb-2">{t("auth.vat")}</label>
          <input value={form.vat_number} onChange={update("vat_number")} maxLength={20} placeholder={t("auth.vatPlaceholder")} className="w-full px-4 py-2.5 rounded-md text-sm font-mono-data bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-blue-500" data-testid="profile-vat"/>
        </div>
        <div>
          <label className="label-tech block mb-2">{t("auth.country")}</label>
          <select value={form.country} onChange={update("country")} className="w-full px-3 py-2.5 rounded-md text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-blue-500" data-testid="profile-country">
            {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="label-tech block mb-2">{t("auth.address")}</label>
        <input value={form.address} onChange={update("address")} maxLength={200} placeholder={t("auth.addressPlaceholder")} className="w-full px-4 py-2.5 rounded-md text-sm bg-slate-900 border border-slate-700 text-slate-100 focus:outline-none focus:border-blue-500" data-testid="profile-address"/>
      </div>
      {error && <div className="p-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="profile-error">{error}</div>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy} className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-50" data-testid="profile-save-btn">
          {busy ? t("common.loading") : t("settings.profileSave")}
        </button>
        {done && (
          <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="profile-saved">
            <Check size={12}/> {t("settings.profileSaved")}
          </span>
        )}
      </div>
    </form>
  );
}
